<script setup lang="ts">
// Signed-in user's dropdown in the app header: Profile, About, Log out.
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'

const authStore = useAuthStore()
const appStore = useAppStore()
const router = useRouter()

const displayName = computed(() => appStore.employeeName || 'Account')

// A signed-in visitor can land on a public page (the help center) before any
// signed-in page has loaded who they are; name them here too.
onMounted(() => {
  if (authStore.isAuthenticated && !appStore.employeeName) {
    appStore.ensureIdentity().catch((err) => console.error(err))
  }
})

// The dropdown-nav component only toggles open/closed on click of its root.
// Closing on mouse-out: if it's open, click it to close.
function closeDropdownOnLeave(e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  if (el.classList.contains('is-active')) el.click()
}

function go(name: string) {
  router.push({ name })
}

// Sign-out is a redirect: the tab goes to Microsoft to have the session dropped
// and comes back to the login page.
async function signOut() {
  appStore.reset()
  await authStore.logOut()
}
</script>

<template>
  <dropdown-nav
    v-if="authStore.isAuthenticated"
    @mouseleave="closeDropdownOnLeave"
  >
    <button type="button">
      <span class="icon">
        <i
          class="fa fa-user-circle"
          aria-hidden="true"
        />
      </span>
      <span>{{ displayName }}</span>
    </button>
    <ul>
      <li>
        <button
          type="button"
          @click="go('profile')"
        >
          Profile
        </button>
      </li>
      <li>
        <button
          type="button"
          @click="go('about')"
        >
          About
        </button>
      </li>
      <li>
        <button
          type="button"
          @click="signOut"
        >
          <span class="icon">
            <i
              class="fas fa-sign-out-alt"
              aria-hidden="true"
            />
          </span>
          <span>Log out</span>
        </button>
      </li>
    </ul>
  </dropdown-nav>
</template>

<style lang="scss" scoped>
/* The menu rows perform JS actions, so they are real <button>s for keyboard
   operability. Strip the native chrome so they read as menu rows. */
.dropdown-nav ul li button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 2rem;
  text-align: left;
  background: none;
  border: none;
  font: inherit;
  line-height: 1.1;
  color: white !important;
  cursor: pointer;
}
.dropdown-nav ul li button:hover,
.dropdown-nav ul li button:focus-visible {
  background-color: #daedfe;
  color: black !important;
}
</style>
